/**
 * Order pricing. Uses our defaults as fallbacks.
 */

import { memo, useCallback } from 'react';

import { DEFAULT_PLATFORM_FEE } from '../lib/constants';
import { Delivery } from '../lib/types';
import { getBaserate } from '../lib/utils';
import { Divider } from './general';

interface PricingProps {
  className?: string;
  delivery: Delivery;

  largeTotal?: boolean;
  tip?: number;
  showTip?: boolean;
  showDeliveryFee?: boolean;
  showTotal?: boolean;
}

const FORMATTER = new Intl.NumberFormat('US', { style: 'currency', currency: 'USD' });

const Pricing: React.FC<PricingProps> = ({
  className,
  delivery,
  largeTotal,
  tip,
  showTip,
  showDeliveryFee,
  showTotal,
}) => {
  const getSubtotal = useCallback(() => {
    let subtotal = getBaserate(delivery.service);
    if (delivery.subtotal) {
      subtotal = delivery.subtotal;
    }

    return subtotal / 100.0;
  }, [delivery]);

  const getDeliveryFee = useCallback(() => {
    if (showDeliveryFee || delivery.deliveryFee || delivery.paid) {
      // Force trusting the delivery even if it's zero.
      return (delivery.deliveryFee || 0) / 100.0;
    }

    // Signal we don't know it yet.
    return -1;
  }, [delivery, showDeliveryFee]);

  const getPlatformFee = useCallback(() => {
    let fee = DEFAULT_PLATFORM_FEE;
    if (delivery.platformFee) {
      fee = delivery.platformFee;
    }

    return fee / 100.0;
  }, [delivery]);

  const getTipAmount = useCallback(() => {
    if (delivery.tip) {
      return delivery.tip / 100.0;
    }
    if (showTip) {
      // Override with prop tip.
      return tip || 0;
    }

    // Signal we don't know it yet.
    return -1;
  }, [delivery, showTip, tip]);

  const getTotal = useCallback(() => {
    if (!delivery.paid && !showTotal) {
      return -1;
    }
    if (delivery.total) {
      return delivery.total / 100.0;
    }

    // Fall back to computing from other values.
    // TODO: If this fails, something bad happened.
    let total = getSubtotal();
    const deliveryFee = getDeliveryFee();
    if (deliveryFee >= 0) {
      total += deliveryFee;
    }
    total += getPlatformFee();
    const tip = getTipAmount();
    if (tip >= 0) {
      total += tip;
    }

    return total;
  }, [delivery, getSubtotal, getDeliveryFee, getPlatformFee, getTipAmount, showTotal]);

  const renderDeliveryFee = useCallback((fee: number) => {
    if (fee < 0) {
      // Not yet determined.
      return 'TBD';
    }
    if (fee === 0) {
      // Free!
      return <span className="text-pink-300 font-medium">Free</span>;
    }

    // Costs something.
    return FORMATTER.format(fee);
  }, []);

  return (
    <div className={`flex flex-col ${className ? className : ''}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">Subtotal</p>
        <p className="text-sm text-gray-400">{FORMATTER.format(getSubtotal())}</p>
      </div>
      <div className="flex items-center justify-between mt-1 lg:mt-2">
        <p className="text-sm text-gray-400">Emotional Labor</p>
        <p className="text-sm text-gray-400">{FORMATTER.format(getPlatformFee())}</p>
      </div>
      <div className="flex items-center justify-between mt-1 lg:mt-2">
        <p className="text-sm text-gray-400">Delivery</p>
        <p className="text-sm text-gray-400">{renderDeliveryFee(getDeliveryFee())}</p>
      </div>
      {getTipAmount() >= 0 || showTip ? (
        <div className="flex items-center justify-between mt-1 lg:mt-2">
          <p className="text-sm text-gray-400">Tip</p>
          <p className="text-sm text-gray-400">{FORMATTER.format(getTipAmount())}</p>
        </div>
      ) : null}

      <Divider className={`${largeTotal ? 'flex mt-5' : 'hidden'}`} />

      <div className={`flex items-center justify-between ${largeTotal ? 'mt-5' : 'mt-1.5 lg:mt-3'}`}>
        <p className={`${largeTotal ? 'text-base' : 'text-sm'} font-medium`}>Total</p>
        <p className={`${largeTotal ? 'text-base' : 'text-sm'} font-medium`}>
          {getTotal() >= 0 ? FORMATTER.format(getTotal()) : 'TBD'}
        </p>
      </div>
    </div>
  );
};

export default memo(Pricing);
